import Autocomplete from '@mui/material/Autocomplete';
import React from 'react';
import * as Yup from 'yup';
import type {
  AttachmentSummary,
  Job,
  JobStatus as NumericJobStatus,
  Metres,
  MillimetresSquared,
} from '../../app/ApiGen';
import { FileAttachmentsValue } from '../../components/CustomFields/FileAttachments';
import {
  type AutocompleteContents,
  AutocompleteValue,
  renderAutocompleteInput,
} from '../../components/CustomFields/SimpleAutocomplete';
import { SwitchInput, SwitchValue } from '../../components/CustomFields/Switch';
import { TextField } from '../../components/TextField';
import type { InputComponentProps, ValueComponentProps } from '../../design-system';
import type { FileAttachment } from '../file';
import { chargePointHasAttributes, userHasAttributes } from '../format';
import { combineSchemas, createSchema, type FieldDefinition, type FormFields } from '../formUtils';
import type { Normalised } from '../request';
import { sortByEmail } from '../sortBy';
import { jobDetailsFields, powerSensorCountValidation, referenceIdValidation } from './CreateJob';
import {
  type AssignedInstallers,
  assignedInstallersValidationSchema,
  getInstallerLabel,
  type InstallerDetails,
} from './installer';

interface JobChargePoint {
  id: string;
  serial: string;
}

export type JobAttachments = {
  attachments: FileAttachment[];
};

export type InstallationDetails = {
  chargePoint: JobChargePoint | null;
  installer: InstallerDetails | null;
  referenceId: string | null;
  supplyCurrentLimit: number | null;
  powerSensorCount: number;
  // Status is optional because it has no corresponding FormFields entry; only present for validation schema purposes.
  status?: NumericJobStatus;
};

export type EvnexInstallationDetails = InstallationDetails & {
  assignedInstallers: InstallerDetails[];
  duration: number | null;
  cableConductorSize: number | null;
  totalCableRun: number | null;
  notes: string;
  isThirdParty: boolean;
};

export const installationDetailsFields = (): FormFields<InstallationDetails> => ({
  referenceId: jobDetailsFields.referenceId,
  installer: {
    id: 'installer',
    label: 'Submitted by',
    valueComponent: ({ value }: ValueComponentProps<InstallerDetails | null>) => (
      <AutocompleteValue value={value} label={value ? getInstallerLabel(value) : 'n/a'} />
    ),
    inputComponent: ({ value }: InputComponentProps<InstallerDetails | null>) => (
      <TextField disabled fullWidth value={value ? getInstallerLabel(value) : ''} />
    ),
  },
  chargePoint: {
    id: 'chargePoint',
    label: 'Installed charge point',
    valueComponent: ({ value }: ValueComponentProps<JobChargePoint | null>) => (
      <>{value?.serial ?? value?.id ?? 'n/a'}</>
    ),
    inputComponent: ({ value }: InputComponentProps<JobChargePoint | null>) => (
      <TextField disabled fullWidth value={value?.serial ?? value?.id ?? ''} />
    ),
  },
  supplyCurrentLimit: { id: 'supplyCurrentLimit', label: 'Supply current limit (A)', inputProps: { disabled: true } },
  powerSensorCount: {
    id: 'powerSensorCount',
    label: 'How many power sensors installed',
    inputProps: { type: 'number' },
  },
});

export const assignedInstallersField: (installerOptions: InstallerDetails[]) => FieldDefinition<AssignedInstallers> = (
  installerOptions,
) => ({
  id: 'assignedInstallers',
  label: 'Installer(s)',
  inputComponent: ({
    disabled,
    error,
    helperText,
    value: selectedInstallers,
    name,
    onChange,
  }: InputComponentProps<InstallerDetails[]>) => (
    <Autocomplete<InstallerDetails, true, true>
      disableClearable
      disableCloseOnSelect
      fullWidth
      getOptionLabel={getInstallerLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      handleHomeEndKeys
      multiple
      onChange={(event, changedInstallers) => {
        onChange(name)({ ...event, target: { ...event.target, value: changedInstallers } });
      }}
      options={installerOptions.sort(sortByEmail('email'))}
      renderInput={renderAutocompleteInput(disabled, error, helperText)}
      selectOnFocus
      value={selectedInstallers.sort(sortByEmail('email'))}
    />
  ),
  valueComponent: ({ value }: AutocompleteContents<InstallerDetails[]>) => (
    <AutocompleteValue value={value} label={getInstallerLabel} />
  ),
});

const cableConductorSizeOptions: number[] = [2.5, 4.0, 6.0, 10.0, 16.0, 25.0];

export const evnexInstallationDetailsFields = (
  installerOptions: InstallerDetails[],
): FormFields<EvnexInstallationDetails> => {
  const { chargePoint, installer, powerSensorCount, referenceId, supplyCurrentLimit } = installationDetailsFields();
  return {
    referenceId,
    isThirdParty: {
      id: 'isThirdParty',
      label: 'Third-party job',
      inputComponent: SwitchInput,
      valueComponent: ({ value }: ValueComponentProps<boolean>) => (
        <SwitchValue value={value} labels={{ true: 'Yes', false: 'No' }} />
      ),
    },
    assignedInstallers: assignedInstallersField(installerOptions),
    installer,
    chargePoint,
    duration: { id: 'duration', label: 'Time spent onsite (h)' },
    cableConductorSize: {
      id: 'cableConductorSize',
      label: 'Cable conductor size (mm²)',
      inputComponent: ({ disabled, error, helperText, value, name, onChange }: InputComponentProps<number | null>) => (
        <Autocomplete<number, false, false>
          fullWidth
          getOptionLabel={(option) => option.toString()}
          handleHomeEndKeys
          onChange={(event, changedOptions) => {
            onChange(name)({ ...event, target: { ...event.target, value: changedOptions } });
          }}
          options={cableConductorSizeOptions}
          renderInput={renderAutocompleteInput(disabled, error, helperText)}
          selectOnFocus
          value={value}
        />
      ),
      valueComponent: ({ value }: AutocompleteContents<number | null>) => (
        <AutocompleteValue value={value} label={value?.toString()} />
      ),
    },
    totalCableRun: { id: 'totalCableRun', label: 'Total cable run (m)' },
    supplyCurrentLimit,
    notes: { id: 'notes', label: 'Job notes', inputProps: { multiline: true } },
    powerSensorCount,
  };
};

export const installationDetailsValidationSchema = combineSchemas(
  combineSchemas(referenceIdValidation, powerSensorCountValidation),
  createSchema<InstallationDetails>({
    supplyCurrentLimit: Yup.number().optional().nullable(true),
  }),
);

const minimumCableConductorSize = 2.5 as MillimetresSquared;
const maximumCableConductorSize = 25 as MillimetresSquared;

const minimumTotalCableRun = 1 as Metres;
const maximumTotalCableRun = 100 as Metres;

const cableMeasurementsSchema = createSchema<EvnexInstallationDetails>({
  cableConductorSize: Yup.number()
    .nullable(true)
    .typeError('Cable conductor size must be a number')
    .min(minimumCableConductorSize, `Must be at least ${minimumCableConductorSize}mm²`)
    .max(maximumCableConductorSize, `Must be ${maximumCableConductorSize}mm² or less`),
  totalCableRun: Yup.number()
    .nullable(true)
    .typeError('Total cable run must be a number')
    .integer('Must be a whole number')
    .min(minimumTotalCableRun, `Must be at least ${minimumTotalCableRun}m`)
    .max(maximumTotalCableRun, `Must be ${maximumTotalCableRun}m or less`),
});

export const evnexInstallationDetailsValidationSchema = combineSchemas(
  combineSchemas(installationDetailsValidationSchema, cableMeasurementsSchema),
  assignedInstallersValidationSchema,
);

export const attachedFilesField: FormFields<JobAttachments> = {
  attachments: { id: 'attachments', label: 'Attached files', valueComponent: FileAttachmentsValue },
};

function getJobInstaller(jobData: Normalised<Job>): InstallerDetails | null {
  const { installer } = jobData;

  if (userHasAttributes(installer)) {
    return { email: installer.email, id: installer.id, name: installer.name ?? '' };
  }

  if (installer === null) {
    return null;
  }

  return { email: '', id: installer.id, name: '' };
}

function getJobChargePoint(jobData: Normalised<Job>): JobChargePoint | null {
  const { chargePoint } = jobData;
  const chargePointSerial = chargePointHasAttributes(chargePoint) ? chargePoint.serial : undefined;
  return chargePoint ? { id: chargePoint.id, serial: chargePointSerial ?? '' } : null;
}

export const toJobAttachmentsField = (attachments: AttachmentSummary[] | undefined): FileAttachment[] =>
  attachments
    ? attachments
        .map((attachment) => ({ filename: attachment.attributes.fileName, fileUrl: attachment.id }))
        .sort((a, b) => a.filename.localeCompare(b.filename))
    : [];

export function toInstallationDetailsFields(jobData: Normalised<Job>): Required<EvnexInstallationDetails> {
  const {
    assignedInstallers,
    details: { cableConductorSize, houseMaxCurrent, totalCableRun },
    duration,
    notes,
    referenceId,
    distributionBoards,
    isThirdParty,
    status,
  } = jobData;
  const [firstDistBoard] = distributionBoards;
  return {
    assignedInstallers: assignedInstallers
      .map((user) =>
        userHasAttributes(user)
          ? { email: user.email, id: user.id, name: user.name ?? '' }
          : { email: '', id: user.id, name: '' },
      )
      .sort(sortByEmail('email')),
    chargePoint: getJobChargePoint(jobData),
    cableConductorSize: cableConductorSize ?? null,
    duration: duration ?? null,
    supplyCurrentLimit: houseMaxCurrent ?? firstDistBoard?.supplyCurrentLimit ?? null,
    installer: getJobInstaller(jobData),
    isThirdParty,
    notes: notes ?? '',
    referenceId: referenceId ?? '',
    status,
    totalCableRun: totalCableRun ?? null,
    powerSensorCount: firstDistBoard?.powerSensorCount ?? 0,
  };
}
